import { smoothness } from "features/onboarding/visuals/live/path";
import type { Story } from "features/onboarding/visuals/live/story";

/**
 * Where things sit in a scene's chart, in CSS px (the SVG is drawn 1:1).
 *
 * fixed: nothing slides. The price draws left to right across the plot at one
 * speed: the faint past fills the first 20% at the start of the loop, the line
 * reaches 95% at the last event and rests just short of the edge.
 *
 * scroll: a live feed. "Now" stays at 74% of the plot and the past slides
 * left. Used only where the whole story would not fit the plot smoothly (a
 * narrow card).
 */
export interface Geometry {
  mode: "fixed" | "scroll";
  W: number;
  H: number;
  /** Right edge of the plot; the axis labels sit to its right. */
  R: number;
  /** Horizontal speed (px per second), the same all loop long. */
  px: number;
  /** x of time 0 (fixed), or of "now" (scroll). */
  x0: number;
  /** fixed: the line stops growing at this time and rests. */
  stop: number;
  top: number;
  bottom: number;
  /** Label size (px). */
  font: number;
  /** A phone's narrow axis: short level names, no prices. */
  compact: boolean;
}

/** The sharpest turn the line may take between two 2px segments. */
export const MAX_TURN = 6;

const PAST = 0.2;
const LAST = 0.95;
const REST = 0.98;

/** The time of the story's last event (fill or alert). */
export const lastEvent = (story: Story): number =>
  Math.max(
    ...story.fills.map((f) => f.t),
    ...(story.markers ?? []).map((m) => m.t)
  );

const build = (
  mode: Geometry["mode"],
  story: Story,
  W: number,
  H: number,
  axis: number,
  phone: boolean
): Geometry => {
  const R = W - axis;
  const font = phone ? 11 : 11.5;
  if (mode === "fixed") {
    const px = ((LAST - PAST) * R) / lastEvent(story);
    return {
      mode,
      W,
      H,
      R,
      px,
      x0: PAST * R,
      stop: ((REST - PAST) * R) / px,
      top: 14,
      bottom: H - 16,
      font,
      compact: phone,
    };
  }
  const x0 = Math.round(R * 0.74);
  return {
    mode,
    W,
    H,
    R,
    // About 6.5 seconds of history; never so slow that the waves bunch up.
    px: Math.max(52, x0 / 6.5),
    x0,
    stop: Infinity,
    top: 14,
    bottom: H - 16,
    font,
    compact: phone,
  };
};

/** How sharply the drawn line turns at most, over a whole loop. */
export const maxTurn = (story: Story, g: Geometry): number => {
  const py = (g.bottom - g.top) / (story.range[1] - story.range[0]);
  const from = -g.x0 / g.px - 0.5;
  return smoothness(story.price, from, Math.min(story.loop, g.stop), g.px, py)
    .maxTurn;
};

/** The fixed window where the story fits it smoothly; otherwise the live feed. */
export const geometry = (
  story: Story,
  W: number,
  H: number,
  axis: number,
  phone: boolean
): Geometry => {
  const fixed = build("fixed", story, W, H, axis, phone);
  return maxTurn(story, fixed) <= MAX_TURN
    ? fixed
    : build("scroll", story, W, H, axis, phone);
};

/**
 * The axis column: wide enough for "Take-profit 62,450"; on a phone only the
 * short name ("Entry 2 ✓", "TP") and the moving price tag.
 */
export const axisWidth = (named: boolean, phone: boolean): number =>
  named ? (phone ? 86 : 136) : phone ? 74 : 84;

export const chartHeight = (strip: boolean, phone: boolean): number =>
  phone ? (strip ? 210 : 236) : strip ? 244 : 286;
